import { UI, HStack, VStack } from '@genial/components';
import { BoxBorder, Color, Spacing, Typography } from '@genial/design-tokens';
import { IconButton } from 'native-base';
import React, { useState } from 'react';

import { OperationType } from '../../../../components/OperationType';

import { AssetDetails, AssetDetailsTypes } from './components/AssetDetails';
import { OrderItem } from './components/OrderItem';

export declare namespace OrderAccordionTypes {
  export interface AssetProps<T> extends AssetDetailsTypes.GetterProps<T> {
    assetTypeLabel: string;
    requestedAmount: string;
    orders: T[];
  }
  export interface Props<T extends any[]> {
    isMobile?: boolean;
    clientName: string;
    clientAccount: string;
    clientAccountLabel?: string;
    totalRequestedAmount: string;
    lastUpdateDate: string;
    sideDescription?: string | null;
    assets: { [K in keyof T]: AssetProps<T[K]> };
    startAccordionOpen?: boolean;
    updateCurrentOpenState?: (isOpen: boolean) => void;
  }
}

export const OrderAccordion = <T extends any[]>({
  assets,
  isMobile,
  clientAccount,
  clientName,
  lastUpdateDate,
  totalRequestedAmount,
  updateCurrentOpenState,
  sideDescription,
  startAccordionOpen = false,
  clientAccountLabel = 'Conta',
}: OrderAccordionTypes.Props<T>) => {
  const [isOpen, setIsOpen] = useState(startAccordionOpen);

  const handlePress = () => {
    setIsOpen((prevState) => !prevState);
    updateCurrentOpenState?.(!isOpen);
  };

  return (
    <VStack space={Spacing['stack.nano']}>
      <UI.Shadow level={1} borderRadius={BoxBorder.heavy}>
        <VStack>
          <VStack
            m={Spacing['inset.sm']}
            space={Spacing['stack.xxxs']}
            borderRadius={BoxBorder.heavy}
            {...(isOpen && {
              borderBottomRadius: 0,
              borderBottomWidth: '1px',
              borderBottomColor: Color['neutral.lightest'],
              pb: Spacing['stack.xxxs'],
            })}>
            <HStack alignItems="center" justifyContent="space-between">
              <VStack space={Spacing['stack.quarck']}>
                <UI.Text color="neutral.darkest">{clientName}</UI.Text>
                <UI.Text color="neutral.dark" fontSize={Typography['fontsize.xs']}>
                  {clientAccountLabel} {clientAccount}
                </UI.Text>
              </VStack>
              <HStack space={Spacing['inline.xxs']}>
                {!isMobile && (
                  <>
                    {sideDescription && (
                      <OrderItem label="Tipo" value={<OperationType title={sideDescription} />} />
                    )}
                    <OrderItem label="Total Enviado" value={totalRequestedAmount} />
                    <OrderItem label="Últ. Atualização" value={lastUpdateDate} />
                  </>
                )}
                <IconButton
                  p={0}
                  variant="unstyled"
                  onPress={handlePress}
                  icon={
                    <UI.Icon
                      name={isOpen ? 'ChevronUp' : 'ChevronDown'}
                      size="xxs"
                      color={Color['neutral.black']}
                    />
                  }
                />
              </HStack>
            </HStack>
            {!!isMobile && (
              <VStack space={Spacing['stack.nano']}>
                {sideDescription && (
                  <OrderItem
                    label="Tipo"
                    value={<OperationType title={sideDescription} />}
                    horizontal
                  />
                )}
                <OrderItem label="Total Enviado" value={totalRequestedAmount} horizontal />
                <OrderItem label="Últ. Atualização" value={lastUpdateDate} horizontal />
              </VStack>
            )}
          </VStack>
          {!!isOpen && (
            <VStack>
              {assets.map(({ requestedAmount, ...props }, index) => (
                <AssetDetails
                  key={index.toString()}
                  assetTotalValue={requestedAmount}
                  {...props}
                  isMobile={isMobile}
                />
              ))}
            </VStack>
          )}
        </VStack>
      </UI.Shadow>
    </VStack>
  );
};
